/**
 * NavigationEngine - Professional text navigation with cross-block support
 * 
 * Provides Word-class navigation behavior including arrow keys, word jumping,
 * and intelligent line navigation for legal document editing.
 */

import { LocalDocumentEngine } from '../core/LocalDocumentEngine';
import { SimplifiedCaretSystem } from '../caret/SimplifiedCaretSystem';
import { TextOperations } from '../core/TextOperations';

export class NavigationEngine {
  private documentEngine: LocalDocumentEngine;
  private caretSystem: SimplifiedCaretSystem;
  
  // Navigation state
  private preferredColumn: number = 0; // For up/down navigation
  private lastHorizontalPosition: number = 0;

  constructor(documentEngine: LocalDocumentEngine, caretSystem: SimplifiedCaretSystem) {
    this.documentEngine = documentEngine;
    this.caretSystem = caretSystem;
  }

  // ============================================================================
  // HORIZONTAL NAVIGATION
  // ============================================================================

  /**
   * Move caret left by one character or word
   */
  moveLeft(wordMode: boolean = false): void {
    const currentPosition = this.documentEngine.getCaretPosition();
    console.log(`🔍 [NavigationEngine] moveLeft: current position ${currentPosition}`);
    
    if (currentPosition === 0) return; // Already at start
    
    let newPosition: number;
    
    if (wordMode) {
      // Move to previous word boundary
      newPosition = TextOperations.findPreviousWordBoundary(
        this.documentEngine.getText(),
        currentPosition
      );
    } else {
      // Move one character left
      newPosition = currentPosition - 1;
    }
    
    this.moveToPosition(newPosition);
    this.updatePreferredColumn();
  }

  /**
   * Move caret right by one character or word
   */
  moveRight(wordMode: boolean = false): void {
    const currentPosition = this.documentEngine.getCaretPosition();
    const maxPosition = this.documentEngine.getText().length;
    
    if (currentPosition >= maxPosition) return; // Already at end
    
    let newPosition: number;
    
    if (wordMode) {
      // Move to next word boundary
      newPosition = TextOperations.findNextWordBoundary(
        this.documentEngine.getText(),
        currentPosition
      );
    } else {
      // Move one character right
      newPosition = currentPosition + 1;
    }
    
    this.moveToPosition(Math.min(newPosition, maxPosition));
    this.updatePreferredColumn();
  }

  // ============================================================================
  // VERTICAL NAVIGATION
  // ============================================================================

  /**
   * Move caret up one line
   */
  moveUp(): void {
    const currentPosition = this.documentEngine.getCaretPosition();
    const text = this.documentEngine.getText();
    
    // Find current line
    const currentLine = TextOperations.getLineAt(text, currentPosition);
    
    if (currentLine.start === 0) return; // Already on first line
    
    // Find previous line
    const prevLineEnd = currentLine.start - 1; // Position before newline
    const prevLine = TextOperations.getLineAt(text, prevLineEnd);
    
    // Calculate target position maintaining column preference
    const currentColumn = currentPosition - currentLine.start;
    const effectiveColumn = this.preferredColumn || currentColumn;
    const targetColumn = Math.min(effectiveColumn, prevLine.line.length);
    const newPosition = prevLine.start + targetColumn;
    
    this.moveToPosition(newPosition);
    this.updatePreferredColumn();
  }

  /**
   * Move caret down one line
   */
  moveDown(): void {
    const currentPosition = this.documentEngine.getCaretPosition();
    const text = this.documentEngine.getText();
    
    // Find current line
    const currentLine = TextOperations.getLineAt(text, currentPosition);
    
    // Check if there's a next line
    const nextLineStart = currentLine.end + 1; // Position after newline
    if (nextLineStart >= text.length) return; // Already on last line
    
    // Find next line
    const nextLine = TextOperations.getLineAt(text, nextLineStart);
    
    // Calculate target position maintaining column preference
    const currentColumn = currentPosition - currentLine.start;
    const effectiveColumn = this.preferredColumn || currentColumn;
    const targetColumn = Math.min(effectiveColumn, nextLine.line.length);
    const newPosition = nextLine.start + targetColumn;
    
    this.moveToPosition(newPosition);
    this.updatePreferredColumn();
  }

  // ============================================================================
  // LINE NAVIGATION
  // ============================================================================

  /**
   * Move to start of current line or document
   */
  moveToLineStart(documentMode: boolean = false): void {
    if (documentMode) {
      // Ctrl+Home: Move to document start
      this.moveToPosition(0);
    } else {
      // Home: Move to line start
      const currentPosition = this.documentEngine.getCaretPosition();
      const text = this.documentEngine.getText();
      const line = TextOperations.getLineAt(text, currentPosition);
      
      this.moveToPosition(line.start);
    }
    
    this.updatePreferredColumn();
  }

  /**
   * Move to end of current line or document
   */
  moveToLineEnd(documentMode: boolean = false): void {
    if (documentMode) {
      // Ctrl+End: Move to document end
      this.moveToPosition(this.documentEngine.getText().length);
    } else {
      // End: Move to line end
      const currentPosition = this.documentEngine.getCaretPosition();
      const text = this.documentEngine.getText();
      const line = TextOperations.getLineAt(text, currentPosition);
      
      this.moveToPosition(line.end);
    }
    
    this.updatePreferredColumn();
  }

  // ============================================================================
  // PAGE NAVIGATION
  // ============================================================================

  /**
   * Move up one page
   */
  movePageUp(): void {
    const linesPerPage = this.calculateLinesPerPage();
    const currentPosition = this.documentEngine.getCaretPosition();
    const text = this.documentEngine.getText();
    
    // Find current line number
    const currentLineNumber = TextOperations.getLineNumber(text, currentPosition);
    
    // Calculate target line
    const targetLineNumber = Math.max(1, currentLineNumber - linesPerPage);
    const targetPosition = this.getPositionAtLine(targetLineNumber);
    
    this.moveToPosition(targetPosition);
  }

  /**
   * Move down one page
   */
  movePageDown(): void {
    const linesPerPage = this.calculateLinesPerPage();
    const currentPosition = this.documentEngine.getCaretPosition();
    const text = this.documentEngine.getText();
    
    // Find current line number
    const currentLineNumber = TextOperations.getLineNumber(text, currentPosition);
    
    // Calculate target line
    const totalLines = TextOperations.countLines(text);
    const targetLineNumber = Math.min(totalLines, currentLineNumber + linesPerPage);
    const targetPosition = this.getPositionAtLine(targetLineNumber);
    
    this.moveToPosition(targetPosition);
  }

  // ============================================================================
  // SMART NAVIGATION
  // ============================================================================

  /**
   * Jump to specific line number (for legal document navigation)
   */
  jumpToLine(lineNumber: number): void {
    const position = this.getPositionAtLine(lineNumber);
    this.moveToPosition(position);
    this.updatePreferredColumn();
  }

  /**
   * Jump to specific character position
   */
  jumpToPosition(position: number): void {
    this.moveToPosition(position);
    this.updatePreferredColumn();
  }

  /**
   * Find and jump to text
   */
  findAndJumpTo(searchText: string, startFrom: number = 0): boolean {
    const text = this.documentEngine.getText();
    const foundPositions = TextOperations.searchText(text, searchText, false);
    
    // Find first occurrence after startFrom
    const targetRange = foundPositions.find(range => range.start >= startFrom);
    
    if (targetRange !== undefined) {
      this.moveToPosition(targetRange.start);
      return true;
    }
    
    return false;
  }

  // ============================================================================
  // LEGAL DOCUMENT NAVIGATION
  // ============================================================================

  /**
   * Navigate to next legal citation
   */
  jumpToNextCitation(): boolean {
    const text = this.documentEngine.getText();
    const currentPosition = this.documentEngine.getCaretPosition();
    const citations = TextOperations.findLegalCitations(text);
    
    const nextCitation = citations.find(citation => citation.start > currentPosition);
    
    if (nextCitation) {
      this.moveToPosition(nextCitation.start);
      return true;
    }
    
    return false;
  }

  /**
   * Navigate to next contract clause
   */
  jumpToNextClause(): boolean {
    const text = this.documentEngine.getText();
    const currentPosition = this.documentEngine.getCaretPosition();
    const clauses = TextOperations.findContractClauses(text);
    
    const nextClause = clauses.find(clause => clause.start > currentPosition);
    
    if (nextClause) {
      this.moveToPosition(nextClause.start);
      return true;
    }
    
    return false;
  }

  /**
   * Navigate to signature block
   */
  jumpToSignatureBlock(): boolean {
    const text = this.documentEngine.getText();
    const signatures = TextOperations.findSignatureBlocks(text);
    
    if (signatures.length > 0) {
      this.moveToPosition(signatures[0].start);
      return true;
    }
    
    return false;
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  private moveToPosition(position: number): void {
    const safePosition = Math.max(0, Math.min(position, this.documentEngine.getText().length));
    
    this.documentEngine.moveCaretTo(safePosition);
    // Note: SimplifiedCaretSystem position updates are handled by InputManager
    
    console.log(`[NavigationEngine] Moved to position ${safePosition}`);
  }

  private updatePreferredColumn(): void {
    const currentPosition = this.documentEngine.getCaretPosition();
    const text = this.documentEngine.getText();
    const line = TextOperations.getLineAt(text, currentPosition);
    
    this.preferredColumn = currentPosition - line.start;
    this.lastHorizontalPosition = currentPosition;
  }

  private calculateLinesPerPage(): number {
    // Estimate lines per page based on viewport height (fallback to 800px)
    const viewportHeight = (typeof window !== 'undefined') ? window.innerHeight : 800;
    const lineHeight = 20; // Standard line height
    
    return Math.max(1, Math.floor(viewportHeight / lineHeight) - 2); // Leave margin
  }

  private getPositionAtLine(lineNumber: number): number {
    const text = this.documentEngine.getText();
    const lines = text.split('\n');
    
    if (lineNumber < 1 || lineNumber > lines.length) {
      return this.documentEngine.getCaretPosition(); // Invalid line, stay put
    }
    
    // Calculate position at start of target line
    let position = 0;
    for (let i = 0; i < lineNumber - 1; i++) {
      position += lines[i].length + 1; // +1 for newline character
    }
    
    // Maintain preferred column if possible
    const targetLine = lines[lineNumber - 1];
    const columnPosition = Math.min(this.preferredColumn, targetLine.length);
    
    return position + columnPosition;
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get current navigation state
   */
  getNavigationState(): {
    currentPosition: number;
    preferredColumn: number;
    currentLine: number;
    totalLines: number;
  } {
    const currentPosition = this.documentEngine.getCaretPosition();
    const text = this.documentEngine.getText();
    
    return {
      currentPosition,
      preferredColumn: this.preferredColumn,
      currentLine: TextOperations.getLineNumber(text, currentPosition),
      totalLines: TextOperations.countLines(text)
    };
  }

  /**
   * Set preferred column for vertical navigation
   */
  setPreferredColumn(column: number): void {
    this.preferredColumn = Math.max(0, column);
  }
}
